import { dataLayerConfig } from '@aspendental/shared-components-web';
import { Box, List, ListItem, ListItemText, Stack, Typography } from '@mui/material';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useEffect } from 'react';

import ScheduleQuestion from '@src/components/ScheduleQuestion';
import { usePatientInformationStore } from '@src/store';
import useAppointmentInformationStore from '@src/store/appointment-information.store';
import useScheduleQuestionsStore from '@src/store/schedule-questions.store';
import { handlePageLeave } from '@src/utils/handle-page-leave';

export default function QuestionnaireStep() {
	const { questionsAnswers, questions, unansweredQuestions } = useScheduleQuestionsStore((store) => store);
	const { salesforceS20, leadId, salesforceTier } = usePatientInformationStore((store) => store);
	const { presetCenterID, presetCenterName } = useAppointmentInformationStore();
	const { t } = useTranslation('index');
	const { locale } = useRouter();

	useEffect(() => {
		dataLayerConfig({
			event: 'scheduleform_step1',
		});

		if (presetCenterID) {
			dataLayerConfig({
				event: 'appointment_step',
				step_name: 'start_about_you',
				lead_id: leadId,
				lead_tier: salesforceS20 && salesforceS20 != 21 ? salesforceTier : 'T1',
				lead_segment: salesforceS20 ?? '21',
				website_selected_center_name: presetCenterName,
				website_selected_center_id: presetCenterID,
				language_preference: locale ?? 'en-US',
			});
		} else {
			// if presetCenterID is not set within 3 seconds, fire event without center info as backup
			const gaTimeout = setTimeout(() => {
				dataLayerConfig({
					event: 'appointment_step',
					step_name: 'start_about_you',
					lead_id: leadId,
					lead_tier: salesforceS20 && salesforceS20 != 21 ? salesforceTier : 'T1',
					lead_segment: salesforceS20 ?? '21',
					website_selected_center_name: null,
					website_selected_center_id: null,
					language_preference: locale ?? 'en-US',
				});
			}, 3000);

			return () => clearTimeout(gaTimeout);
		}
	}, [presetCenterID]);

	useEffect(() => {
		document.addEventListener('click', handlePageLeave, true);

		return () => {
			document.removeEventListener('click', handlePageLeave, true);
		};
	}, [questionsAnswers]);

	useEffect(() => {
		if (unansweredQuestions.length) {
			dataLayerConfig({
				event: 'scheduleform_step1_questionsnotcompleted',
			});
		}
	}, [unansweredQuestions]);

	return (
		<>
			<Stack direction="column" spacing={2.5} pt={2.5} gap={{ md: 2 }}>
				{unansweredQuestions.length > 0 && (
					<Box
						role="alert"
						sx={{
							borderWidth: 1,
							borderStyle: 'solid',
							borderColor: 'error.dark',
							borderRadius: '4px',
							padding: '14px 16px',
							backgroundColor: 'error.light',
						}}
						data-test-id="unanswered-questions-box"
					>
						<Typography variant="bodyMediumSemiBold" sx={{ color: 'error.dark' }}>
							{t('provideAnswers')}
						</Typography>
						<Stack direction="column" spacing={1} mt={1.5}>
							<List sx={{ pl: '20px', listStyle: 'disc' }}>
								{unansweredQuestions.map((question) => (
									<ListItem
										key={question.short_name}
										sx={{
											display: 'list-item',
											'&::marker': {
												color: 'error.dark',
											},
										}}
										disablePadding
										data-test-id={`unanswered-question-${question.id}`}
									>
										<ListItemText
											primary={question.question_text}
											primaryTypographyProps={{
												variant: 'bodyMediumBook',
												sx: { color: 'error.dark' },
											}}
										/>
									</ListItem>
								))}
							</List>
						</Stack>
					</Box>
				)}

				{(questions ?? []).map((question, index) => (
					<ScheduleQuestion
						key={`schedule-question-${index}`}
						unanswered={unansweredQuestions?.find((q) => q.short_name === question.short_name)}
						{...question}
						data-test-id={`schedule-question-${index}`}
					/>
				))}
			</Stack>
		</>
	);
}
